import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // Capture phase: 'toggle' does not bubble, but capture still descends to us
    this.addEventListener('toggle', this.#onDetailsToggle, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
  }

  /**
   * @returns {boolean} Whether this instance is the desktop flyout variant
   */
  get #isFlyout() {
    return this.dataset.flyout === 'true';
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // The flyout has no top level to step back to, so Escape closes the whole panel
    this.#close(this.#isFlyout ? this.refs.details : this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // Closing the drawer resets every open section; restore the ones set to open by default
    if (details === this.refs.details) {
      for (const section of details.querySelectorAll('details[data-default-open]')) {
        section.setAttribute('open', '');
        section.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      }
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
        // Auto-expand first nested accordion if configured and no accordion already open
        try {
          // The flyout expands the section chosen in openSection() instead
          if (this.dataset.expandFirstSubAccordion === 'true' && !this.#isFlyout) {
            // Look for any details elements inside submenu accordions that are not already open
            const existingOpen = details.querySelector('accordion-custom details[open]');
            if (!existingOpen) {
              const firstAccordionDetails = details.querySelector('.menu-drawer__submenu accordion-custom details:not([open])');
              if (firstAccordionDetails instanceof HTMLDetailsElement) {
                firstAccordionDetails.setAttribute('open','');
              }
              // Fallback: if submenu structure has been flattened (no .menu-drawer__submenu container)
              // attempt to open the first top-level accordion details so the setting still has an effect.
              else {
                const fallbackFirst = details.querySelector('.menu-drawer__navigation accordion-custom details:not([open])');
                if (fallbackFirst instanceof HTMLDetailsElement) {
                  fallbackFirst.setAttribute('open','');
                }
              }
            }
          }
        } catch(e) { /* silent */ }
      }, 0);
    });
  }

  /**
   * Open the desktop flyout to a specific top-level menu section.
   * Bound from the inline header links via on:click="#HeaderFlyout/openSection";
   * the clicked trigger carries the target link handle in data-flyout-section.
   * @param {Event} [event]
   */
  openSection(event) {
    // The declarative event system proxies `target` to the element carrying the
    // on:click attribute (currentTarget would be document — it delegates globally)
    const trigger = event?.target instanceof HTMLElement ? event.target.closest('[data-flyout-section]') : null;
    const handle = trigger instanceof HTMLElement ? (trigger.dataset.flyoutSection ?? null) : null;

    if (!this.isOpen) {
      // Triggers live outside the <details>, so native summary toggling never
      // runs for them; set the open state before running the shared open logic.
      this.refs.details.setAttribute('open', '');
      this.open();
    }

    this.#activateSection(handle);
  }

  /**
   * Expand the drawer section matching the given link handle and collapse the rest.
   * Works for both the accordion markup (menus < 3 levels) and the slide-in
   * submenu markup (3-level menus), which both give the top-level summary/anchor
   * an id of "HeaderDrawer-<handle>".
   * @param {string | null} handle
   */
  #activateSection(handle) {
    const { details } = this.refs;
    const target = handle ? details.querySelector(`[id="HeaderDrawer-${CSS.escape(handle)}"]`) : null;
    const targetDetails = target?.closest('details');

    const openSections = details.querySelectorAll(
      'accordion-custom > details[open], details.menu-drawer__menu-container[open]'
    );
    for (const section of openSections) {
      if (section === targetDetails) continue;
      section.classList.remove('menu-open');
      section.removeAttribute('open');
      section.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }

    if (targetDetails && targetDetails !== details && !targetDetails.hasAttribute('open')) {
      targetDetails.setAttribute('open', '');
      targetDetails.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      if (targetDetails.classList.contains('menu-drawer__menu-container')) {
        // 3-level submenus animate via the same class the summary click path adds
        requestAnimationFrame(() => targetDetails.classList.add('menu-open'));
      }
    }

    // Slide-in submenus anchor to the navigation box; make sure it isn't scrolled
    this.querySelector('.menu-drawer__navigation')?.scrollTo?.(0, 0);

    this.#updateFlyoutPromos();
  }

  /**
   * Sync everything that reflects the currently open section: the header and
   * tab-row triggers' aria-expanded state, and which promo cards show (cards
   * with no match label always show).
   */
  #updateFlyoutPromos() {
    if (!this.#isFlyout) return;

    const openSummary = this.refs.details.querySelector(
      'accordion-custom > details[open] > summary, details.menu-drawer__menu-container[open] > summary'
    );
    const activeHandle = openSummary?.id?.replace('HeaderDrawer-', '') ?? '';

    this.#syncTriggerState(activeHandle || null);

    const promos = this.querySelector('.flyout-promos');
    if (!promos) return;

    let visible = 0;
    for (const card of promos.querySelectorAll('.flyout-promo-card')) {
      const match = card.dataset.promoMatch ?? '';
      const show = match === '' || match === activeHandle;
      card.hidden = !show;
      if (show) visible += 1;
    }
    promos.hidden = visible === 0;
  }

  /**
   * Mirror the open section on the inline triggers' aria-expanded state.
   * @param {string | null} activeHandle
   */
  #syncTriggerState(activeHandle) {
    if (!this.#isFlyout) return;

    for (const trigger of document.querySelectorAll('[data-flyout-section]')) {
      trigger.setAttribute('aria-expanded', trigger.dataset.flyoutSection === activeHandle ? 'true' : 'false');
    }
  }

  /**
   * Keep promos and trigger state in sync when sections are toggled from inside
   * the drawer, and reset triggers when the drawer closes.
   * @param {Event} event
   */
  #onDetailsToggle = (event) => {
    if (!this.#isFlyout) return;

    if (event.target === this.refs.details) {
      if (!this.refs.details.open) this.#syncTriggerState(null);
    } else {
      this.#updateFlyoutPromos();
    }
  };

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
